import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LayoutTemplate, Columns2, AlignJustify, Palette } from "lucide-react";
import {
	CvSectionBlock,
	SectionKey,
	SectionConfigMap,
	CvLayout,
	CustomColors,
	CV_COLOR_SCHEMES,
	CV_LAYOUTS,
	CvColorScheme,
} from "@/types/cvTemplate";
import { CvSectionCard } from "./CvSectionCard";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface CvBuilderPanelProps {
	sections: CvSectionBlock[];
	onSectionsChange: (sections: CvSectionBlock[]) => void;
	colorScheme: string;
	onColorSchemeChange: (scheme: string) => void;
	customColors: CustomColors;
	onCustomColorsChange: (colors: CustomColors) => void;
	layout: CvLayout;
	onLayoutChange: (layout: CvLayout) => void;
	disabled?: boolean;
}

const LAYOUT_ICONS: Record<CvLayout, typeof LayoutTemplate> = {
	standard: AlignJustify,
	sidebar: Columns2,
	compact: LayoutTemplate,
};

export function CvBuilderPanel({
	sections,
	onSectionsChange,
	colorScheme,
	onColorSchemeChange,
	customColors,
	onCustomColorsChange,
	layout,
	onLayoutChange,
	disabled,
}: CvBuilderPanelProps) {
	const { t } = useTranslation();

	// DnD state
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [dropIndex, setDropIndex] = useState<number | null>(null);
	const dragNodeRef = useRef<HTMLDivElement | null>(null);

	const sorted = [...sections].sort((a, b) => a.order - b.order);

	const handleToggle = useCallback((key: SectionKey, enabled: boolean) => {
		onSectionsChange(sections.map(s =>
			s.key === key ? { ...s, enabled } : s
		));
	}, [sections, onSectionsChange]);

	const handleConfigChange = useCallback((key: SectionKey, config: SectionConfigMap[SectionKey]) => {
		onSectionsChange(sections.map(s =>
			s.key === key ? { ...s, config } : s
		));
	}, [sections, onSectionsChange]);

	const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
		setDragIndex(index);
		dragNodeRef.current = e.currentTarget;
		e.dataTransfer.effectAllowed = "move";
		requestAnimationFrame(() => {
			if (dragNodeRef.current)
				dragNodeRef.current.style.opacity = "0.4";
		});
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		if (dragIndex === null || index === dragIndex) {
			setDropIndex(null);
			return;
		}
		setDropIndex(index);
	}, [dragIndex]);

	const handleDrop = useCallback((_e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
		if (dragIndex === null || dragIndex === targetIndex) return;
		const reordered = [...sorted];
		const [moved] = reordered.splice(dragIndex, 1);
		reordered.splice(targetIndex, 0, moved);
		onSectionsChange(reordered.map((block, i) => ({ ...block, order: i })));
		setDragIndex(null);
		setDropIndex(null);
	}, [dragIndex, sorted, onSectionsChange]);

	const handleDragEnd = useCallback(() => {
		if (dragNodeRef.current)
			dragNodeRef.current.style.opacity = "1";
		setDragIndex(null);
		setDropIndex(null);
		dragNodeRef.current = null;
	}, []);

	return (
		<div className="space-y-5">
			{/* Layout Picker */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">{t("admin.cvBuilder.layout", "Layout")}</Label>
				<div className="grid grid-cols-3 gap-2">
					{CV_LAYOUTS.map(l => {
						const Icon = LAYOUT_ICONS[l.key];
						return (
							<button
								key={l.key}
								type="button"
								onClick={() => onLayoutChange(l.key)}
								disabled={disabled}
								className={cn(
									"flex flex-col items-center gap-1.5 p-3 rounded-sm border text-xs transition-all",
									layout === l.key
										? "border-primary bg-primary/5 ring-1 ring-primary"
										: "border-border hover:border-primary/50"
								)}
							>
								<Icon className="h-5 w-5" />
								<span className="font-medium">{l.label}</span>
								<span className="text-[10px] text-muted-foreground">{l.description}</span>
							</button>
						);
					})}
				</div>
			</div>

			{/* Color Scheme Picker */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">{t("admin.cvBuilder.colorScheme", "Color Scheme")}</Label>
				<div className="flex flex-wrap gap-2">
					{CV_COLOR_SCHEMES.map((scheme: CvColorScheme) => (
						<button
							key={scheme.key}
							type="button"
							onClick={() => onColorSchemeChange(scheme.key)}
							className={cn(
								"flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-medium transition-all",
								colorScheme === scheme.key
									? "border-primary bg-primary/5 ring-1 ring-primary"
									: "border-border hover:border-primary/50"
							)}
							disabled={disabled}
						>
							<div
								className="w-3.5 h-3.5 rounded-full border"
								style={{ backgroundColor: scheme.primary }}
							/>
							{scheme.label}
						</button>
					))}
					{/* Custom option */}
					<button
						type="button"
						onClick={() => onColorSchemeChange("custom")}
						className={cn(
							"flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-medium transition-all",
							colorScheme === "custom"
								? "border-primary bg-primary/5 ring-1 ring-primary"
								: "border-border hover:border-primary/50"
						)}
						disabled={disabled}
					>
						<Palette className="h-3.5 w-3.5" />
						{t("admin.cvBuilder.customColors", "Custom")}
					</button>
				</div>

				{/* Custom color inputs */}
				{colorScheme === "custom" && (
					<div className="grid grid-cols-4 gap-3 pt-2">
						{(["primary", "secondary", "background", "text"] as const).map(key => (
							<div key={key} className="space-y-1">
								<Label className="text-[10px] capitalize text-muted-foreground">
									{key === "primary" ? t("admin.cvBuilder.colorAccent", "Accent")
										: key === "secondary" ? t("admin.cvBuilder.colorSubtle", "Subtle")
										: key === "background" ? t("admin.cvBuilder.colorBackground", "Background")
										: t("admin.cvBuilder.colorText", "Text")}
								</Label>
								<div className="flex items-center gap-1.5">
									<input
										type="color"
										value={customColors[key]}
										onChange={(e) => onCustomColorsChange({ ...customColors, [key]: e.target.value })}
										className="w-8 h-8 rounded border cursor-pointer bg-transparent"
										disabled={disabled}
									/>
									<span className="text-[10px] font-mono text-muted-foreground">{customColors[key]}</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Sections */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">{t("admin.cvBuilder.sections", "Sections")}</Label>
				<p className="text-xs text-muted-foreground">{t("admin.cvBuilder.dragToReorder", "Drag to reorder. Toggle sections on or off.")}</p>
				<div className="space-y-1.5">
					{sorted.map((block, index) => (
						<CvSectionCard
							key={block.key}
							block={block}
							onToggle={handleToggle}
							onConfigChange={handleConfigChange}
							isDragTarget={dropIndex === index && dragIndex !== null}
							disabled={disabled}
							onDragStart={(e) => handleDragStart(e as any, index)}
							onDragOver={(e) => handleDragOver(e as any, index)}
							onDrop={(e) => handleDrop(e as any, index)}
							onDragEnd={handleDragEnd}
						/>
					))}
				</div>
			</div>
		</div>
	);
}
